import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { LifeBuoy } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjdXN0b21lciUyMHN1cHBvcnQlMjBhZ2VudCUyMHRlYW0lMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzg3NTc4MzU2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8">
        <form data-testid="login-form" onSubmit={submit} className="w-full max-w-sm space-y-6">
          <div className="flex items-center gap-2">
            <LifeBuoy className="h-7 w-7 text-orange-500" />
            <span className="font-extrabold text-xl tracking-tight">Chronodesk</span>
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Sign in</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Helpdesk tickets, task board & time billing — one command center.
            </p>
          </div>
          {error && (
            <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="text-xs font-mono-digits uppercase tracking-widest text-muted-foreground">Email</label>
              <input
                data-testid="email-input"
                type="email"
                required
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-md bg-secondary/60 border border-border px-3 py-2.5 text-sm outline-none focus:border-orange-500/60 transition-colors"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-xs font-mono-digits uppercase tracking-widest text-muted-foreground">Password</label>
              <input
                data-testid="password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-md bg-secondary/60 border border-border px-3 py-2.5 text-sm outline-none focus:border-orange-500/60 transition-colors"
                placeholder="••••••••"
              />
            </div>
          </div>
          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white font-semibold text-sm py-2.5 transition-all disabled:opacity-50"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Support command center" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <p className="font-mono-digits text-xs uppercase tracking-widest text-orange-400">Email → Ticket → Task → Time → Payout</p>
          <p className="text-2xl font-bold mt-2 max-w-md">Track every minute. Pay every agent fairly.</p>
        </div>
      </div>
    </div>
  );
}
